import { useState } from "react";
import { useLoginResult } from "../State/LoginResultContext";
import { useEffect } from "react";
import { API_URL } from "../utils/constants";
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatUsageNumber(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "0";
  return Math.abs(n % 1) < 1e-9 ? n.toFixed(0) : n.toFixed(1);
}

function UsageRing({ usedHours = 0, totalHours = 0 }) {
  const safeTotal = Math.max(0, Number(totalHours) || 0);
  const safeUsed = Math.max(0, Number(usedHours) || 0);
  const progress = safeTotal > 0 ? Math.min(1, safeUsed / safeTotal) : 0;

  // Visual tuning to match screenshots
  const size = 160;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  const centerLabel = (
    <p className="atkinson-font tracking-tight">
      <span className="text-[32px]">{formatUsageNumber(safeUsed)}/</span>
      <span className="text-[20px]">{formatUsageNumber(safeTotal)}</span>
    </p>
  );

  return (
    <div className="relative w-40 h-40 sm:w-44 sm:h-44 mx-auto">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-full"
        role="img"
        aria-label={`Monthly usage ${centerLabel} hours`}
      >
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke="#DADADA94"
          strokeWidth={stroke}
        />

        {/* Progress ring */}
        {progress > 0 ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#5701AE"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ) : null}
      </svg>

      {/* Center labels */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-white font-semibold text-xl leading-none">
          {centerLabel}
        </div>
        <div className="mt-1 text-[10px] text-white">Hours Used</div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { loginResults } = useLoginResult();
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [usageSummary, setUsageSummary] = useState({
    usedHours: 0,
    totalHours: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAnalytics = async () => {
      const url = API_URL + `/users/${loginResults.activeOrganization.id}/usageAnalytics`;
      const token = loginResults.token;
      setLoading(true);
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.status === 200) {
          setUsageAnalytics(data);
          const usedHours = Number(data?.usageAnalytics?.hoursUsed ?? 0);
          const totalHours = Number(data?.totalHours ?? 30);
          setUsageSummary({
            usedHours: Number.isFinite(usedHours) ? usedHours : 0,
            totalHours: Number.isFinite(totalHours) ? totalHours : 0,
          });
        } 
      } catch (error) {
        console.log("usageAnalytics error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (loginResults?.id && loginResults?.token) {
      fetchUserAnalytics();
    } else {
      setLoading(false);
    }
  }, [loginResults?.id, loginResults?.token]);

  return (
    <div className="atkinson-font w-full">
      <main className="w-full pb-12">
        <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center pt-4 sm:pt-10">
            <h1 className="text-[32px] sm:text-[40px] font-semibold tracking-tight text-white">
              Usage Overview
            </h1>
            <div className="mt-4 sm:mt-8 text-[32px] font-semibold text-white tracking-tight">
              {loginResults?.activeOrganization.name || "[Org Name]"}
            </div>
            <div className="sm:mt-4 text-sm text-white tracking-tight">
              Customer Status:{" "}
              <span className="text-white">
                {loginResults?.customerStatus || "Standard"}
              </span>
            </div>
          </div>

          <div className="mt-6 sm:mt-12 mx-auto w-full max-w-2xl">
            {loading ? (
              <>
                <div
                  className={classNames(
                    "rounded-2xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm shadow-2xl shadow-black/30",
                    "px-4 py-5 sm:px-8 sm:py-8",
                  )}
                >
                  <div className="h-7 w-48 mx-auto rounded bg-white/10 animate-pulse" />
                  <div className="mt-8 flex items-center justify-center">
                    <div className="w-40 h-40 sm:w-44 sm:h-44 rounded-full bg-white/10 animate-pulse" />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="rounded-xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm p-2 sm:px-6 sm:py-5 text-center">
                    <div className="h-8 w-12 mx-auto rounded bg-white/10 animate-pulse" />
                    <div className="mt-2 h-3 w-full max-w-[120px] mx-auto rounded bg-white/10 animate-pulse" />
                  </div>
                  <div className="rounded-xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm p-2 sm:px-6 sm:py-5 text-center">
                    <div className="h-8 w-12 mx-auto rounded bg-white/10 animate-pulse" />
                    <div className="mt-2 h-3 w-full max-w-[140px] mx-auto rounded bg-white/10 animate-pulse" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className={classNames(
                    "rounded-2xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm shadow-2xl shadow-black/30",
                    "px-4 py-5 sm:px-8 sm:py-8",
                  )}
                >
                  <h2 className="text-center text-2xl font-semibold text-white">
                    Current Monthly Usage
                  </h2>
                  <div className="mt-8 flex items-center justify-center">
                    <UsageRing
                      usedHours={usageSummary.usedHours}
                      totalHours={usageSummary.totalHours}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="rounded-xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm p-2 sm:px-6 sm:py-5 text-center">
                    <div className="text-3xl sm:text-[32px] font-bold text-white tracking-tight">
                      {usageAnalytics?.usageAnalytics?.numOfEvents ?? "-"}
                    </div>
                    <div className="sm:mt-2 text-[10px] text-white">
                      Total # of Events this month
                    </div>
                  </div>
                  <div className="rounded-xl border border-[#6A6680] bg-[#230046] backdrop-blur-sm p-2 sm:px-6 sm:py-5 text-center">
                    <div className="text-3xl sm:text-[32px] font-bold text-white tracking-tight">
                      {usageAnalytics?.usageAnalytics?.numOfConnections ?? "-"}
                    </div>
                    <div className="sm:mt-2 text-[10px] text-white">
                      Total # of Connections this month
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
